import React, { useEffect, useCallback } from 'react';
import { useDarkMode } from '../../utils/hooks/useDarkMode';
import { useBookingFormData } from '../../utils/hooks/useBookingFormData';
import { useBookingValidation } from '../../utils/hooks/useBookingValidation';
import { useBookingSubmit } from '../../utils/hooks/useBookingSubmit';
import {
    FormHeader,
    LocationSection,
    TripDetailsSection,
    DateTimeSection,
    FormFooter,
} from './booking';
import styles from '../../styles/components/forms/BookingForm.module.css';

/**
 * BookingForm component for booking trips with validation
 */
const BookingForm = () => {
    const { isDarkMode } = useDarkMode();
    const {
        formData,
        errors,
        setErrors,
        isClicked,
        setIsClicked,
        timeError,
        setTimeError,
        todayDate,
        now,
        handleInputChange,
        handleDateChange,
        setDefaultTime,
        handlePlaceSelect,
        handleErrorChange,
    } = useBookingFormData();

    // Function to clear specific field error
    const handleClearError = useCallback((field) => {
        // Only clear error if it exists and is not a time validation error
        if (errors[field] && errors[field] !== 'Please select a time from now onwards for today.') {
            setErrors(prev => ({
                ...prev,
                [field]: '',
            }));
        }
        
        // Also clear paste and manipulation errors for the field
        const pasteErrorKey = `${field}_pasteError`;
        const manipulationErrorKey = `${field}_manipulationError`;
        
        if (errors[pasteErrorKey]) {
            setErrors(prev => ({
                ...prev,
                [pasteErrorKey]: '',
            }));
        }
        
        if (errors[manipulationErrorKey]) {
            setErrors(prev => ({
                ...prev,
                [manipulationErrorKey]: '',
            }));
        }
    }, [errors, setErrors]);

    const { validateForm } = useBookingValidation(formData, todayDate, errors, setErrors, setTimeError);
    const { handleSubmit, confirmDialog } = useBookingSubmit(formData, validateForm, setIsClicked, isDarkMode);

    const getInputClassName = fieldName => {
        const hasError = errors[fieldName] && isClicked;
        const darkModeClass = isDarkMode ? styles['input-dark'] : '';
        
        // Fields that need padding: 0
        const noPaddingFields = ['departureDate', 'departureTime', 'returnDate', 'returnTime'];
        const paddingStyle = noPaddingFields.includes(fieldName) ? 'p-0' : '';
        
        switch (fieldName) {
            case 'tripType':
            case 'noOfPassengers':
            case 'departureDate':
            case 'departureTime':
            case 'returnDate':
            case 'returnTime':
            case 'pickUpAddress':
            case 'dropOffAddress':
                return `${styles['input-text']} data-hj-allow' ${hasError ? styles['border-red'] : styles['border-gray']} ${darkModeClass} ${paddingStyle}`;

            default:
                return `${styles['input-places']} data-hj-allow' ${hasError ? styles['border-red'] : styles['border-gray']} ${darkModeClass} ${paddingStyle}`;
        }
    };

    // useEffect(() => {
    //     const log = (e) => console.log("Focus changed to:", e.target);
    //     window.addEventListener("focusin", log);
    //     return () => window.removeEventListener("focusin", log);
    // }, []);

    return (
        <>
            <div className={`${styles['form-container']} ${isDarkMode ? styles['form-container-dark'] : ''}`} id="booking-form">
                <form 
                    className="row" 
                    onSubmit={handleSubmit}
                    role="form"
                    aria-labelledby="booking-form-title"
                    aria-describedby="form-errors"
                    noValidate
                >
                    <FormHeader isDarkMode={isDarkMode} />

                    <div className={`col-12 pt-3 ${styles['form-content']}`}>
                        <div className="row">
                            <LocationSection
                                formData={formData}
                                errors={errors}
                                isClicked={isClicked}
                                isDarkMode={isDarkMode}
                                onPlaceSelect={handlePlaceSelect}
                                onClearError={handleClearError}
                                onErrorChange={handleErrorChange}
                                getInputClassName={getInputClassName}
                            />

                            <TripDetailsSection
                                formData={formData}
                                errors={errors}
                                isClicked={isClicked}
                                isDarkMode={isDarkMode}
                                onInputChange={handleInputChange}
                                onClearError={handleClearError}
                                getInputClassName={getInputClassName}
                            />

                            <DateTimeSection
                                formData={formData}
                                errors={errors}
                                isClicked={isClicked}
                                isDarkMode={isDarkMode}
                                todayDate={todayDate}
                                now={now}
                                onDateChange={handleDateChange}
                                onInputChange={handleInputChange}
                                onSetDefaultTime={setDefaultTime}
                                onClearError={handleClearError}
                                getInputClassName={getInputClassName}
                            />
                        </div>

                        <FormFooter
                            isDarkMode={isDarkMode}
                            timeError={timeError}
                            onSubmit={handleSubmit}
                        />
                    </div>
                </form>
            </div>
            {confirmDialog}
        </>
    );
};

export default BookingForm;
